import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Loader2, AlertTriangle, Search } from "lucide-react";
import { CRM_API, errMsg } from "../api";

const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm";

// ------------------------------------------------------------------
// Searchable picker with inline "add new"
// ------------------------------------------------------------------
function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, onClose]);
}

export function SearchSelect({ placeholder, fetchResults, renderItem, onSelect, onAddNew, addLabel, selectedLabel, onClear, disabled, testId }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const fetchRef = useRef(fetchResults);
  fetchRef.current = fetchResults;
  useOutsideClose(ref, () => setOpen(false));

  useEffect(() => {
    if (!open || disabled) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try { setResults(await fetchRef.current(query)); }
      catch { /* keep previous results */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, disabled]);

  const pick = (item) => { onSelect(item); setOpen(false); setQuery(""); };

  if (selectedLabel) {
    return (
      <div className="flex items-center gap-2 bg-[#1F5EFF]/10 border border-[#1F5EFF]/40 rounded-lg px-3 py-2" data-testid={`${testId}-selected`}>
        <span className="text-sm text-white flex-1 truncate">{selectedLabel}</span>
        <button type="button" onClick={onClear} className="text-white/40 hover:text-white" data-testid={`${testId}-clear`}><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
      <input
        value={query}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        className={inp + " pl-9 disabled:opacity-40"}
        data-testid={testId}
      />
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-[#1b1b23] border border-white/12 rounded-lg shadow-2xl max-h-56 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {loading && <div className="px-3 py-2.5 text-xs text-white/40 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2.5 text-xs text-white/40">No matches found</div>}
          {!loading && results.map((item) => renderItem(item, pick))}
          <button type="button" onClick={() => { setOpen(false); onAddNew(); }} data-testid={`${testId}-add-new`}
            className="w-full text-left px-3 py-2.5 text-sm text-[#8db2ff] hover:bg-white/5 border-t border-white/10 flex items-center gap-2 sticky bottom-0 bg-[#1b1b23]">
            <Plus className="w-4 h-4" /> {addLabel}
          </button>
        </div>
      )}
    </div>
  );
}

// Checks for an existing customer with the same phone (digit comparison).
// Returns the existing customer object or null.
export async function findDuplicateByPhone(phone) {
  const digits = (s) => (s || "").replace(/\D/g, "");
  if (!digits(phone)) return null;
  const { data } = await CRM_API.get(`/customers?q=${encodeURIComponent(phone.trim())}&limit=10`);
  return data.find((c) => digits(c.phone) && digits(c.phone) === digits(phone)) || null;
}

export function NewCustomerForm({ onCreated, onCancel, allowDuplicate = true }) {
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (force) => {
    if (!f.first_name.trim()) { toast.error("First name is required"); return; }
    if (f.phone.trim().length < 4) { toast.error("Phone is required"); return; }
    setBusy(true);
    try {
      if (!force) {
        const exact = await findDuplicateByPhone(f.phone);
        if (exact) { setDup(exact); return; }
      }
      const payload = {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim() || null,
        address: f.address.trim() || null,
        notes: f.notes.trim() || null,
      };
      const { data } = await CRM_API.post("/customers", payload);
      toast.success("Customer created");
      onCreated(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 rounded-lg bg-white/3 border border-white/10 space-y-2.5" data-testid="new-customer-form">
      {dup && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm space-y-2" data-testid="new-customer-dup-warning">
          <div className="flex items-start gap-2 text-amber-300">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Customer with this phone already exists: <b>{dup.first_name} {dup.last_name}</b> · {dup.phone}</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => onCreated(dup)} className="px-3 py-1.5 rounded bg-[#1F5EFF] hover:bg-[#1F5EFF]/80 text-white text-xs" data-testid="new-customer-use-existing">Use existing customer</button>
            {allowDuplicate && (
              <button type="button" onClick={() => save(true)} disabled={busy} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 text-white/70 text-xs" data-testid="new-customer-create-anyway">Create anyway</button>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2.5">
        <input value={f.first_name} onChange={set("first_name")} placeholder="First name *" className={inp} data-testid="new-customer-first-name" />
        <input value={f.last_name} onChange={set("last_name")} placeholder="Last name" className={inp} data-testid="new-customer-last-name" />
        <input value={f.phone} onChange={set("phone")} placeholder="Phone *" className={inp} data-testid="new-customer-phone" />
        <input value={f.email} onChange={set("email")} placeholder="Email" type="email" className={inp} data-testid="new-customer-email" />
      </div>
      <input value={f.address} onChange={set("address")} placeholder="Address" className={inp} data-testid="new-customer-address" />
      <textarea rows={2} value={f.notes} onChange={set("notes")} placeholder="Notes" className={inp + " resize-y"} data-testid="new-customer-notes" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-white/50 hover:text-white text-xs" data-testid="new-customer-cancel">Cancel</button>
        <button type="button" onClick={() => save(false)} disabled={busy} className="px-4 py-1.5 rounded bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-xs font-medium disabled:opacity-40" data-testid="new-customer-save">
          {busy ? "…" : "Add customer"}
        </button>
      </div>
    </div>
  );
}

export function NewVehicleForm({ customerId, onCreated, onCancel }) {
  const [f, setF] = useState({ vin: "", year: "", make: "", model: "", trim: "", license_plate: "", color: "" });
  const [busy, setBusy] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const decode = async () => {
    if (f.vin.trim().length < 11) { toast.error("Enter a VIN (11–17 characters)"); return; }
    setDecoding(true);
    try {
      const { data } = await CRM_API.post("/vin/decode", { vin: f.vin.trim() });
      setF((p) => ({
        ...p,
        year: data.year ? String(data.year) : p.year,
        make: data.make || p.make,
        model: data.model || p.model,
        trim: data.trim || p.trim,
      }));
      toast.success("VIN decoded — verify the fields");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDecoding(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        vin: f.vin.trim() || null,
        license_plate: f.license_plate.trim() || null,
        year: f.year ? Number(f.year) : null,
        make: f.make.trim() || null,
        model: f.model.trim() || null,
        trim: f.trim.trim() || null,
        color: f.color.trim() || null,
      };
      const { data } = await CRM_API.post("/vehicles", payload);
      toast.success("Vehicle added");
      onCreated(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 rounded-lg bg-white/3 border border-white/10 space-y-2.5" data-testid="new-vehicle-form">
      <div className="flex gap-2">
        <input value={f.vin} onChange={set("vin")} placeholder="VIN" maxLength={17} className={inp} data-testid="new-vehicle-vin" />
        <button type="button" onClick={decode} disabled={decoding} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-xs whitespace-nowrap disabled:opacity-40 flex items-center gap-1.5" data-testid="new-vehicle-decode">
          {decoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Decode VIN
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        <input value={f.year} onChange={set("year")} placeholder="Year" type="number" min="1900" max="2100" className={inp} data-testid="new-vehicle-year" />
        <input value={f.make} onChange={set("make")} placeholder="Make" className={inp} data-testid="new-vehicle-make" />
        <input value={f.model} onChange={set("model")} placeholder="Model" className={inp} data-testid="new-vehicle-model" />
        <input value={f.trim} onChange={set("trim")} placeholder="Trim" className={inp} data-testid="new-vehicle-trim" />
        <input value={f.license_plate} onChange={set("license_plate")} placeholder="License plate" className={inp} data-testid="new-vehicle-plate" />
        <input value={f.color} onChange={set("color")} placeholder="Color" className={inp} data-testid="new-vehicle-color" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-white/50 hover:text-white text-xs" data-testid="new-vehicle-cancel">Cancel</button>
        <button type="button" onClick={save} disabled={busy} className="px-4 py-1.5 rounded bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-xs font-medium disabled:opacity-40" data-testid="new-vehicle-save">
          {busy ? "…" : "Add vehicle"}
        </button>
      </div>
    </div>
  );
}
